import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { lazy, Suspense } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  toggleTradingAuditAction,
  tradingPairStateAtom,
} from '@root/features/trade/trading'
import { TradingPairMetricsPanel } from '@root/features/trending-pairs'
import { TradeChartSkeleton } from '@root/widgets/trade/trade-chart/ui/desktop/trade-chart-skeleton'

import {
  compactChartHeightAtom,
  mainChartHeightAtom,
  pairViewAtom,
} from '../../atoms'

import { TradingPairHeader } from './trading-pair-header'
import { TradingPairPoolInfo } from './trading-pair-header/trading-pair-pool-info'
import { TradingPairViewSkeleton } from './trading-pair-view-skeleton'

import type {
  BuyControlsModel,
  SellControlsModel,
} from '@root/features/trade/trading-controls/atoms/common'

type TradingPairViewProps = {
  chartVariant?: 'minimal' | 'full'
  buyControlsModel: BuyControlsModel
  sellControlsModel: SellControlsModel
}

const CHART_MIN_HEIGHT = 120

// The chart widget carries two.js and lightweight-charts. Statically
// imported here, they were downloaded by every session that opened the app,
// not by the ones that opened a pair.
const TradeChart = lazy(() =>
  import('@root/widgets/trade/trade-chart/ui/mobile/trade-chart').then(
    (module_) => ({ default: module_.TradeChart }),
  ),
)

export const TradingPairView: React.FC<TradingPairViewProps> = ({
  chartVariant = 'full',
  buyControlsModel,
  sellControlsModel,
}) => {
  const [pairView] = useAtom(pairViewAtom)
  const [selectedPairState] = useAtom(tradingPairStateAtom)

  const toggleShowAudit = useAction(toggleTradingAuditAction)

  const detailsExpanded = pairView === 'details'

  if (AsyncStateN.isError(selectedPairState)) {
    // Error is shown on screen level
    return
  }

  if (AsyncStateN.isSuccess(selectedPairState)) {
    const pair = selectedPairState.value
    return (
      <Root>
        <TradingPairHeader />
        <AnimatePresence initial={false}>
          {pairView === 'chart' ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              key="metrics-panel"
            >
              <TradingPairPoolInfo
                pair={pair}
                onAuditClick={() => {
                  toggleShowAudit()
                }}
              />
            </motion.div>
          ) : undefined}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {detailsExpanded ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              key="metrics-panel"
            >
              <TradingPairMetricsPanel pair={pair} />
            </motion.div>
          ) : undefined}
        </AnimatePresence>

        <Suspense fallback={<TradeChartSkeleton height={CHART_MIN_HEIGHT} />}>
          <TradeChart
            pair={pair}
            buyControlsModel={buyControlsModel}
            sellControlsModel={sellControlsModel}
            heightAtom={
              chartVariant === 'full'
                ? mainChartHeightAtom
                : compactChartHeightAtom
            }
            minHeight={CHART_MIN_HEIGHT}
            maxHeight={window.innerHeight * 0.65}
            showToolbar={chartVariant === 'full'}
          />
        </Suspense>
      </Root>
    )
  }

  return <TradingPairViewSkeleton />
}

const Root = styled.div``
